import * as React from 'react'
import { RouteDataItem } from '../../types/Core/Routes'
import { routeData } from './RouteData'

export const normalizePath = (path: string): string => {
	return path.replace('/*', '').replace('/:id?', '').toLowerCase()
}

export const isRouteAvailableForTemplate = (
	route: Pick<RouteDataItem, 'templates'>,
	projectTemplate: number | undefined,
): boolean => {
	if (projectTemplate === undefined) return true
	return route.templates.includes(projectTemplate)
}

const findRouteDataForPath = (
	path: string,
	role?: string,
): RouteDataItem | undefined => {
	const normalized = normalizePath(path)
	const candidates = role
		? routeData.filter((route) => route.roles.includes(role as RouteDataItem['roles'][number]))
		: routeData

	const exact = candidates.find((route) => normalizePath(route.routesPath) === normalized)
	if (exact) return exact

	const prefixMatches = candidates
		.filter((route) => {
			const routePath = normalizePath(route.routesPath)
			return normalized.startsWith(routePath + '/')
		})
		.sort((a, b) => normalizePath(b.routesPath).length - normalizePath(a.routesPath).length)

	return prefixMatches[0]
}

export const shouldHideRoutePathForTemplate = (
	path: unknown,
	projectTemplate: number | undefined,
	role?: string,
): boolean => {
	if (typeof path !== 'string') return false
	if (projectTemplate === undefined) return false

	const matchedRoute = findRouteDataForPath(path, role)
	if (!matchedRoute) return false

	return !isRouteAvailableForTemplate(matchedRoute, projectTemplate)
}

export const filterRoutesForProjectTemplate = (
	routes: React.ReactNode[],
	projectTemplate: number | undefined,
	role?: string,
): React.ReactNode[] => {
	return routes.filter((route) => {
		if (!React.isValidElement(route)) return true
		return !shouldHideRoutePathForTemplate(route.props.path, projectTemplate, role)
	})
}

/** Removes index routes so a template/default Navigate can own `/`. */
export const stripIndexRoutes = (routes: React.ReactNode[]): React.ReactNode[] => {
	return routes.filter((route) => {
		if (!React.isValidElement(route)) return true
		const { path, index } = route.props as { path?: string; index?: boolean }
		return path != null || index !== true
	})
}

export const getDefaultRouteForRole = (
	routes: RouteDataItem[],
): RouteDataItem | undefined => {
	return routes.find((route) => route.defaultRoute)
}

/** Landing path after selecting a project (token refresh). */
export const getPostProjectSelectPath = (
	role: string,
	projectId: number,
	projectTemplate: number | undefined,
): string => {
	if (role === 'ProjectControls' || role === 'OfficeAdmin') {
		return `/projects/${projectId}/details`
	}

	if (projectTemplate === 1) {
		const defaultRoute = getDefaultRouteForRole(
			routeData
				.filter((route) => route.roles.includes(role as RouteDataItem['roles'][number]))
				.filter((route) => isRouteAvailableForTemplate(route, projectTemplate)),
		)
		if (defaultRoute) return defaultRoute.path
	}

	if (role === 'Foreman') return '/backlog'
	return '/workitems'
}

export const filterDrawerRoutesForProjectTemplate = <T extends Pick<RouteDataItem, 'templates'>>(
	drawerRoutes: T[],
	projectTemplate: number | undefined,
): T[] => {
	return drawerRoutes.filter((route) => isRouteAvailableForTemplate(route, projectTemplate))
}

// Finds Route components (from the ProjectControls set) that match a RouteDataItem routesPath.
// Used to map permission-filtered route data items to actual <Route> nodes.
export const findRouteComponentsByPath = (
	routesPath: string,
	allRouteNodes: Record<string, React.ReactNode[]>,
): React.ReactNode[] => {
	const matchingRoutes: React.ReactNode[] = []
	const targetPath = normalizePath(routesPath)

	// Only search through ProjectControls routes since it has all routes
	const projectControlsRoutes = allRouteNodes['ProjectControls'] ?? []

	projectControlsRoutes.forEach((route) => {
		if (React.isValidElement(route)) {
			const routePath = route.props.path
			if (routePath && normalizePath(routePath) === targetPath) {
				matchingRoutes.push(route)
			}
		}
	})

	return matchingRoutes
}
